/**
 * Connector product URL template — Plan-ConnectorProductUrlTemplate.md.
 *
 * A connector's API often returns a handle (`urlHandle: "test-prod"`) rather
 * than an absolute product URL, so the card has nothing to link to. The
 * connector row can carry an optional `card_mapping.product_url_template`
 * (e.g. `https://spago.com/products/{urlHandle}`); the BACKEND renders it into
 * the card's existing `checkout_url` key at runtime — the wire format does not
 * change.
 *
 * The helpers here exist so the Integrations mapping editor can show a live
 * preview of the URL the backend WILL build, without a billed re-inference.
 * They mirror `back/agents/connector_cards.py` (`resolve_path`, `extract_items`,
 * `_build_product_card`) — keep the precedence in `resolveProductUrl` in step
 * with `_build_product_card`, or the preview lies about runtime.
 */

/** Matches a bare key or a `[0]` index — mirrors backend `_PATH_TOKEN_RE`. */
const PATH_TOKEN_RE = /([^.[\]]+)|\[(\d+)\]/g;

/**
 * Resolve a dot/bracket path (`a.b[0].c`) against nested objects/arrays.
 * Returns `undefined` on ANY miss — never throws. Empty path returns `obj`.
 */
export function resolvePath(obj: unknown, path: string | null | undefined): unknown {
  if (path === null || path === undefined || path === '') return obj;
  if (typeof path !== 'string') return undefined;

  let current: unknown = obj;
  PATH_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = PATH_TOKEN_RE.exec(path)) !== null) {
    if (current === null || current === undefined) return undefined;
    const [, key, idx] = m;
    if (idx !== undefined) {
      if (!Array.isArray(current)) return undefined;
      const i = Number(idx);
      if (i >= current.length) return undefined;
      current = current[i];
    } else {
      if (typeof current !== 'object' || Array.isArray(current)) return undefined;
      current = (current as Record<string, unknown>)[key];
    }
  }
  return current;
}

/**
 * Pull the item list out of a raw connector response. A single object at the
 * path is wrapped into a one-element list (mirrors backend `extract_items`).
 */
export function extractItems(response: unknown, itemsPath: string | null | undefined): Record<string, unknown>[] {
  const target = resolvePath(response, itemsPath);
  if (Array.isArray(target)) {
    return target.filter((it): it is Record<string, unknown> =>
      typeof it === 'object' && it !== null && !Array.isArray(it));
  }
  if (typeof target === 'object' && target !== null) return [target as Record<string, unknown>];
  return [];
}

/** Mirrors backend `_URL_FORBIDDEN_RE`. */
const URL_FORBIDDEN_RE = /[\s<>"'`\\^|]/;

/** `{urlHandle}` / `{a.b[0].c}` placeholders, in order of appearance. */
const PLACEHOLDER_RE = /\{([^{}]+)\}/g;

/**
 * True only for an absolute `http(s)` URL carrying a host — mirrors backend
 * `is_absolute_http_url`. Rejects `javascript:`, `data:`, protocol-relative
 * `//host`, scheme-only `https:/x`, whitespace and URL-unsafe punctuation.
 * With `allowPlaceholders`, `{dot.path}` spans are stubbed out first so an
 * un-rendered template can be validated.
 *
 * Keep in step with the backend: a template this accepts but the backend
 * rejects means the editor shows a green preview for a URL that can never save.
 */
export function isAbsoluteHttpUrl(
  value: unknown,
  { allowPlaceholders = false }: { allowPlaceholders?: boolean } = {},
): value is string {
  if (typeof value !== 'string' || !value) return false;
  const probe = allowPlaceholders ? value.replace(PLACEHOLDER_RE, 'x') : value;
  if (probe.includes('{') || probe.includes('}')) return false;
  if (URL_FORBIDDEN_RE.test(probe)) return false;
  // Demanded explicitly: WHATWG `new URL` rewrites `https:/x` into host `x`,
  // where Python's `urlsplit` reports an empty netloc and the backend rejects it.
  if (!/^https?:\/\//i.test(probe)) return false;
  try {
    const parsed = new URL(probe);
    return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && Boolean(parsed.host);
  } catch {
    return false;
  }
}

/**
 * Is this a safe href to put in the DOM? Scheme + host only.
 *
 * Deliberately laxer than `isAbsoluteHttpUrl`: that one mirrors the backend's
 * TEMPLATE validator, whose forbidden-punctuation set (`'`, `^`, `|`, …) exists
 * to keep stored templates tidy. A rendered `checkout_url` — Shopify's included,
 * built server-side and never passed through that validator — may legitimately
 * carry such characters in a path or query, and must not silently lose its link.
 * What actually matters at render time is the scheme: no `javascript:`, no
 * `data:`, no protocol-relative `//host`, no relative handle.
 */
export function isSafeHttpHref(value: unknown): value is string {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return false;
  try {
    return Boolean(new URL(value).host);
  } catch {
    return false;
  }
}

/**
 * `encodeURIComponent` with the sub-delims Python's `quote(v, safe="")` also
 * escapes, so the previewed URL is byte-identical to the one the backend emits.
 */
function quote(value: string): string {
  return encodeURIComponent(value)
    .replace(/[!'()*]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
}

export function extractPlaceholders(template: string): string[] {
  return [...template.matchAll(PLACEHOLDER_RE)].map(m => m[1].trim());
}

/** Only scalars substitute into a URL — an object/array is a mapping mistake. */
function scalar(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return String(value);
  return null;
}

/**
 * Render `product_url_template` against a raw item — mirrors backend
 * `_render_url_template`.
 *
 * Returns `null` — meaning the card gets NO `checkout_url` — when the template
 * is not a safe absolute URL, when ANY placeholder resolves to empty/None (a
 * URL with a hole in it is worse than no link), or when the rendered result
 * fails the absolute-URL check again. Each value is percent-encoded with an
 * empty safe set, so it can never inject a path segment, a query, or a host.
 */
export function renderUrlTemplate(item: unknown, template: string | null | undefined): string | null {
  if (!template || !isAbsoluteHttpUrl(template, { allowPlaceholders: true })) return null;

  let holes = false;
  const rendered = template.replace(PLACEHOLDER_RE, (_full, rawPath: string) => {
    const value = scalar(resolvePath(item, rawPath.trim()));
    if (value === null || value === '') {
      holes = true;
      return '';
    }
    return quote(value);
  });

  if (holes) return null;
  return isAbsoluteHttpUrl(rendered) ? rendered : null;
}

/**
 * The `checkout_url` the backend will put on a product card, for a raw item.
 * Mirrors `_build_product_card` precedence exactly:
 *   1. `fields.url` resolving to an absolute http(s) URL wins verbatim
 *      (back-compat: connectors whose API returns real URLs are untouched).
 *   2. else `product_url_template`, rendered.
 *   3. else no link.
 */
export function resolveProductUrl(
  item: unknown,
  fields: Record<string, string> | undefined,
  template: string | null | undefined,
): string | null {
  const mapped = scalar(resolvePath(item, fields?.url));
  if (isAbsoluteHttpUrl(mapped)) return mapped;
  return renderUrlTemplate(item, template);
}
